import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getClientDashboard, getCatalog, getContainers } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { StatusBadge, TrafficLight, resolveTrafficLight } from '../components/ui/StatusBadge';
import { Button } from '../components/ui/button';
import { formatDate } from '../lib/utils';
import { useI18n } from '../contexts/I18nContext';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Ship,
  Package,
  Clock,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Container expandable sub-row for client view
function ClientContainerSubTable({ mbl, isOpen }) {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && !loaded) {
      loadContainers();
    }
  }, [isOpen, loaded]);

  const loadContainers = async () => {
    setLoading(true);
    try {
      const res = await getContainers(mbl);
      setContainers(res.data);
      setLoaded(true);
    } catch (err) {
      console.error('Error loading containers:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <tr>
      <td colSpan={7} className="p-0 bg-secondary/20">
        <div className="p-3 border-l-4 border-primary/50">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-300 py-2">
              <Loader2 size={14} className="animate-spin" />
              <span className="text-sm">Cargando contenedores...</span>
            </div>
          ) : containers.length === 0 ? (
            <div className="text-sm text-slate-300 py-2">
              Sin contenedores registrados
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 border-b border-border/50">
                  <th className="py-1.5 px-2 text-left font-medium">Contenedor</th>
                  <th className="py-1.5 px-2 text-left font-medium">Estatus</th>
                  <th className="py-1.5 px-2 text-left font-medium">Semáforo</th>
                  <th className="py-1.5 px-2 text-left font-medium">Arribo</th>
                  <th className="py-1.5 px-2 text-left font-medium">Entrega</th>
                  <th className="py-1.5 px-2 text-left font-medium">Días en Puerto</th>
                </tr>
              </thead>
              <tbody>
                {containers.map((container) => (
                  <tr key={container.id} className="border-b border-border/30 hover:bg-secondary/30">
                    <td className="py-1.5 px-2 font-mono text-xs text-foreground">{container.container_number}</td>
                    <td className="py-1.5 px-2">
                      <StatusBadge code={container.status_container_code} label={container.status_label} />
                    </td>
                    <td className="py-1.5 px-2">
                      <TrafficLight status={resolveTrafficLight(container)} />
                    </td>
                    <td className="py-1.5 px-2 text-slate-300">{formatDate(container.arrived_at)}</td>
                    <td className="py-1.5 px-2 text-slate-300">{formatDate(container.delivered_at)}</td>
                    <td className="py-1.5 px-2">
                      {container.days_in_port_label && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          container.days_in_port > 7 ? 'bg-red-500/20 text-red-400' : 
                          container.days_in_port > 3 ? 'bg-amber-500/20 text-amber-400' : 
                          'bg-slate-500/20 text-slate-300'
                        }`}>
                          {container.days_in_port_label}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function ClientDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const { t } = useI18n();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getClientDashboard();
      setData(res.data);
    } catch (err) {
      console.error('Error loading client dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (mbl) => {
    setExpandedRows(prev => ({
      ...prev,
      [mbl]: !prev[mbl]
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-300">Cargando...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-12 text-slate-300">
          No se pudo cargar la información
        </div>
      </DashboardLayout>
    );
  }

  const statusChartData = Object.entries(data.shipments_by_status || {}).map(([code, info]) => ({
    name: info.label || code,
    value: info.count,
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-dashboard-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl">Mi Dashboard</h1>
          <p className="text-muted-foreground">{t('clientDash.subtitle')}</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-sm">
                <Ship className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-heading text-foreground">{data.total_shipments}</p>
                <p className="text-sm text-slate-300">{t('clientDash.activeShipments')}</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-sm">
                <Package className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-heading text-foreground">{data.total_containers}</p>
                <p className="text-sm text-slate-300">Contenedores</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-sm">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-heading text-foreground">{data.containers_in_port}</p>
                <p className="text-sm text-slate-300">{t('clientDash.inProcess')}</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-sm">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-heading text-foreground">{data.delivered_recently}</p>
                <p className="text-sm text-slate-300">{t('clientDash.deliveredMonth')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Status Distribution */}
          <div className="bg-card border border-border rounded-sm p-4">
            <h3 className="font-heading text-lg mb-4">{t('clientDash.statusByShipment')}</h3>
            {statusChartData.length > 0 ? (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '2px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {statusChartData.map((item, idx) => (
                    <div key={item.name} className="flex items-center gap-1.5 text-xs">
                      <span 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="text-muted-foreground">{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                No hay embarques para mostrar
              </div>
            )}
          </div>

          {/* Summary Card */}
          <div className="bg-card border border-border rounded-sm p-4">
            <h3 className="font-heading text-lg mb-4">{t('clientDash.activitySummary')}</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <Ship className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">{t('clientDash.activeShipments')}</span>
                </div>
                <span className="font-heading text-lg">{data.total_shipments}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm">Contenedores Totales</span>
                </div>
                <span className="font-heading text-lg">{data.total_containers}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm">Pendientes en Puerto</span>
                </div>
                <span className="font-heading text-lg">{data.containers_in_port}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-sm">{t('clientDash.delivered')} (30d)</span>
                </div>
                <span className="font-heading text-lg">{data.delivered_recently}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Shipments */}
        <div className="bg-card border border-border rounded-sm">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-heading text-lg text-foreground">{t('clientDash.recentShipments')}</h3>
            <Link to="/shipments">
              <Button variant="ghost" size="sm">
                Ver todos
                <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-border">
                  <th className="py-2 px-3 w-8"></th>
                  <th className="py-2 px-3">MBL/BL</th>
                  <th className="py-2 px-3">Estatus</th>
                  <th className="py-2 px-3">Contenedores</th>
                  <th className="py-2 px-3">Días</th>
                  <th className="py-2 px-3">ETA Final</th>
                  <th className="py-2 px-3">Última Actualización</th>
                </tr>
              </thead>
              <tbody>
                {(!data.recent_shipments || data.recent_shipments.length === 0) ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-300">
                      {t('clientDash.noShipments')}
                    </td>
                  </tr>
                ) : (
                  data.recent_shipments.map((shipment) => (
                    <React.Fragment key={shipment.mbl}>
                      <tr 
                        className="border-b border-border/50 hover:bg-secondary/30 transition-colors cursor-pointer"
                        onClick={() => toggleRow(shipment.mbl)}
                      >
                        <td className="py-2 px-3">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={(e) => { e.stopPropagation(); toggleRow(shipment.mbl); }}
                          >
                            {expandedRows[shipment.mbl] ? 
                              <ChevronDown size={14} /> : 
                              <ChevronRight size={14} />
                            }
                          </Button>
                        </td>
                        <td className="py-2 px-3">
                          <Link 
                            to={`/shipments/${shipment.mbl}`}
                            className="font-mono text-sm text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {shipment.mbl}
                          </Link>
                        </td>
                        <td className="py-2 px-3">
                          <StatusBadge 
                            code={shipment.status_general_code} 
                            label={shipment.status_label} 
                          />
                        </td>
                        <td className="py-2 px-3 text-sm font-mono text-foreground">
                          <span className="flex items-center gap-1">
                            <Package size={12} className="text-blue-400" />
                            {shipment.container_count}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          {shipment.days_total !== null && shipment.days_total !== undefined ? (
                            <span 
                              className={`flex items-center gap-1 text-sm ${shipment.days_status === 'cerrado' ? 'text-slate-400' : 'text-amber-400'}`}
                              title={shipment.days_total_label}
                            >
                              <Clock size={12} />
                              {shipment.days_total_label}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-sm text-slate-300">
                          {formatDate(shipment.eta_final)}
                        </td>
                        <td className="py-2 px-3 text-sm text-slate-300">
                          {formatDate(shipment.last_update_at)}
                        </td>
                      </tr>
                      <ClientContainerSubTable mbl={shipment.mbl} isOpen={expandedRows[shipment.mbl]} />
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
